// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/work-optimization/OptimizationSummaryStrip.jsx

import React from "react";

function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatNumber(value) {
  return numberValue(value).toLocaleString();
}

function formatMinutes(value) {
  const minutes = numberValue(value);
  if (!minutes) return "0 min";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hr`;
}

function plural(value, singular, pluralText = `${singular}s`) {
  return numberValue(value) === 1 ? singular : pluralText;
}

function Metric({ label, value, footnote, tone = "default" }) {
  return (
    <div className={`wo-summary-metric wo-summary-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{footnote}</small>
    </div>
  );
}

function FooterMetric({ label, value }) {
  return (
    <div
      style={{
        display: "grid",
        gap: 3,
        minWidth: 0,
        padding: "10px 12px",
        borderRadius: 14,
        background: "rgba(15, 23, 42, 0.34)",
        border: "1px solid rgba(148, 163, 184, 0.16)",
      }}
    >
      <span
        style={{
          color: "#93a4c6",
          fontSize: 10,
          fontWeight: 900,
          letterSpacing: ".08em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <strong
        style={{
          color: "#eaf0ff",
          fontSize: 13,
          lineHeight: 1.2,
          fontWeight: 850,
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </strong>
    </div>
  );
}

function GuardrailStatus({ guardrails = {} }) {
  const blocked = [
    guardrails.autonomous_execution,
    guardrails.state_mutation,
    guardrails.auto_assignment,
    guardrails.auto_close,
    guardrails.auto_validation,
    guardrails.auto_merge,
    guardrails.workflow_mutation,
    guardrails.priority_mutation,
  ].some(Boolean);

  return (
    <div
      style={{
        display: "grid",
        gap: 4,
        padding: "11px 13px",
        borderRadius: 14,
        background: blocked
          ? "rgba(251, 191, 36, 0.1)"
          : "rgba(15, 28, 56, 0.72)",
        border: blocked
          ? "1px solid rgba(251, 191, 36, 0.28)"
          : "1px solid rgba(96, 165, 250, 0.28)",
        minWidth: 0,
      }}
    >
      <strong
        style={{
          color: blocked ? "#fde68a" : "#eaf0ff",
          fontSize: 13,
          lineHeight: 1.25,
          fontWeight: 900,
        }}
      >
        {blocked ? "Guardrail review needed" : "Advisory-only guardrails active"}
      </strong>
      <small
        style={{
          color: "#a8b3c7",
          fontSize: 12,
          lineHeight: 1.35,
        }}
      >
        OASYS does not execute, assign, close, validate, merge, mutate workflow state, or permanently reorder priorities.
      </small>
    </div>
  );
}

export default function OptimizationSummaryStrip({
  context,
  loading = false,
  error = "",
  onRefresh,
}) {
  const safeContext = context && typeof context === "object" ? context : {};
  const summary = safeContext.summary || {};
  const guardrails = safeContext.guardrails || {};

  const totalTasks = numberValue(summary.total_tasks_analyzed || summary.tasks_analyzed || summary.total_tasks);
  const duplicateWork = numberValue(summary.duplicate_work_items);
  const similarGroups = numberValue(summary.similar_groups);
  const dependencyInsights = numberValue(summary.dependency_insights);

  return (
    <section className="wo-summary-strip" aria-label="Work optimization summary">
      <div className="wo-summary-header">
        <div>
          <span>Work Optimization</span>
          <h3>{loading ? "Optimizing work queue…" : "Operational Intelligence"}</h3>
          <p>
            Advisory sequencing based on impact, effort, readiness, dependencies, duplicate work, and batch opportunities.
          </p>
        </div>
        <button type="button" className="mec-link-button" onClick={onRefresh} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh Optimization"}
        </button>
      </div>

      {error ? <div className="wo-summary-error">{error}</div> : null}

      <div className="wo-summary-grid">
        <Metric label="Quick Wins" value={formatNumber(summary.quick_wins)} footnote="low effort value" tone="ok" />
        <Metric label="Fast Follows" value={formatNumber(summary.fast_follows)} footnote="next best actions" />
        <Metric label="High Impact" value={formatNumber(summary.high_impact)} footnote="priority work" tone="warn" />
        <Metric label="Batches" value={formatNumber(summary.batch_opportunities)} footnote="grouped work" />
        <Metric label="Est. Time Saved" value={formatMinutes(summary.estimated_time_saved_minutes)} footnote="advisory estimate" tone="ok" />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 0.85fr)",
          gap: 12,
          alignItems: "stretch",
          marginTop: 16,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
            gap: 10,
            minWidth: 0,
          }}
        >
          <FooterMetric
            label="Analyzed"
            value={`${formatNumber(totalTasks)} ${plural(totalTasks, "task")}`}
          />
          <FooterMetric
            label="Duplicate Work"
            value={`${formatNumber(duplicateWork)} ${plural(duplicateWork, "item")}`}
          />
          <FooterMetric
            label="Similar Groups"
            value={`${formatNumber(similarGroups)} ${plural(similarGroups, "group")}`}
          />
          <FooterMetric
            label="Dependencies"
            value={`${formatNumber(dependencyInsights)} ${plural(dependencyInsights, "insight")}`}
          />
        </div>

        <GuardrailStatus guardrails={guardrails} />
      </div>
    </section>
  );
}
